/**
 * Qwen3Manager - Singleton manager for Qwen3.5-4B via WebLLM/MLC
 *
 * Replaces SmolLM3Manager. Uses @mlc-ai/web-llm with Azure-hosted model files.
 *
 * Features:
 * - Fetches signed Azure URLs from backend before loading
 * - WebLLM handles WebGPU detection, caching, and worker management internally
 * - OpenAI-compatible chat.completions API for generation
 * - Streaming text generation with thinking/answering state tracking
 * - Singleton pattern (survives component unmount/remount)
 */

import * as webllm from '@mlc-ai/web-llm';
import { ModelLoadProgress } from './MobileQwen3Manager';
import {
  getModelUrls,
  requestPersistentStorage,
  isStoragePersistent,
  clearOldCaches,
} from './hybrid_model_selection';

// ============================================================================
// Constants
// ============================================================================

const QWEN3_MODEL_ID = 'Qwen3.5-4B-q4f16_1-MLC';

// ============================================================================
// Qwen3Manager Class
// ============================================================================

class Qwen3Manager {
  private static instance: Qwen3Manager;
  private engine: webllm.MLCEngineInterface | null = null;
  private isModelReady: boolean = false;
  private initPromise: Promise<void> | null = null;
  private progressCallbacks: Set<(progress: ModelLoadProgress) => void> =
    new Set();

  private constructor() {}

  public static getInstance(): Qwen3Manager {
    if (!Qwen3Manager.instance) {
      Qwen3Manager.instance = new Qwen3Manager();
    }
    return Qwen3Manager.instance;
  }

  // ========================================================================
  // Initialization
  // ========================================================================

  public async initialize(): Promise<void> {
    if (this.isModelReady) {
      console.log('♻️  Qwen3.5 already loaded');
      return;
    }

    if (this.initPromise) {
      console.log('⏳ Qwen3.5 initialization already in progress...');
      return this.initPromise;
    }

    console.log('🆕 Starting Qwen3.5 initialization...');
    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  private async _doInitialize(): Promise<void> {
    const overallStart = performance.now();

    try {
      // Step 1: Request persistent storage
      this.reportProgress({
        loaded: 0, total: 100, percentage: 0,
        stage: 'checking',
        message: 'Checking storage availability...',
      });

      const isPersisted = await isStoragePersistent();
      if (!isPersisted) {
        console.log('🔒 [Qwen3.5] Requesting persistent storage...');
        const granted = await requestPersistentStorage();
        console.log(`🔒 [Qwen3.5] Persistent storage: ${granted ? 'granted' : 'denied'}`);
      }

      await clearOldCaches();

      // Step 2: Fetch signed Azure URLs from backend
      this.reportProgress({
        loaded: 0, total: 100, percentage: 2,
        stage: 'checking',
        message: 'Requesting signed model URLs...',
      });

      console.log('🔑 [Qwen3.5] Requesting signed URLs from backend...');
      const urlsResponse = await getModelUrls('qwen3.5-4b-q4f16-webllm');

      // The backend returns modelUrl like:
      //   "https://productioninference.blob.core.windows.net/models/qwen3-4b-q4f16_1-MLC?sv=2024-...&sig=..."
      // We need the base URL (without SAS ) for WebLLM's model URL,
      // and we'll configure a custom fetch to append the SAS token.
      const fullUrl = urlsResponse.modelUrl;
      const questionMarkIndex = fullUrl.indexOf('?');
      const modelBaseUrl = questionMarkIndex !== -1
        ? fullUrl.substring(0, questionMarkIndex)
        : fullUrl;
      let sasToken = questionMarkIndex !== -1
        ? fullUrl.substring(questionMarkIndex + 1)
        : '';

      console.log(`✅ [Qwen3.5] Got signed URL, base: ${modelBaseUrl.substring(0, 60)}...`);

      // Step 3: Configure WebLLM with Azure-hosted model
      this.reportProgress({
        loaded: 0, total: 100, percentage: 5,
        stage: 'initializing',
        message: 'Configuring WebLLM engine...',
      });

      // Build the appConfig that tells WebLLM where to find the model files.
      // WebLLM fetches files as: model_url + "/resolve/main/" + filename
      // For Azure, we need to intercept this and redirect to:
      //   modelBaseUrl + "/" + filename + "?" + sasToken
      //
      // We achieve this by setting the model URL to the Azure base URL
      // and providing a custom fetch function that handles the URL rewriting.

      // Determine WASM URL — either self-hosted on Azure or from WebLLM CDN
      const wasmUrl = urlsResponse.wasmUrl ||
        `${webllm.modelLibURLPrefix}${webllm.modelVersion}/Qwen3.5-4B-q4f16_1_cs1k-webgpu.wasm`;

      const appConfig: webllm.AppConfig = {
        ...webllm.prebuiltAppConfig,
        model_list: [
          {
            model: modelBaseUrl,
            model_id: QWEN3_MODEL_ID,
            model_lib: wasmUrl,
            overrides: {
              context_window_size: 16384, // Desktop hardcoded ceiling; safely increased for Qwen3.5 4B (was 4096)
            },
          },
        ],
      };

      // Step 4: Create the WebLLM engine with progress tracking
      this.reportProgress({
        loaded: 0, total: 100, percentage: 8,
        stage: 'downloading',
        message: 'Loading Qwen3.5-4B model...',
      });
      
      // Temporarily intercept fetch to inject SAS tokens for Azure Blob requests
      const originalFetch = window.fetch;
      const blobHost = new URL(urlsResponse.modelUrl).origin;

      window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
        // If this is a request to our Azure blob but missing the SAS token, append it
        if (url.startsWith(blobHost) && !url.includes('sig=')) {
          const separator = url.includes('?') ? '&' : '?';
          return originalFetch(url + separator + sasToken, init);
        }
        return originalFetch(input, init);
      };

      // Create the engine — WebLLM handles worker creation, model download,
      // caching, WebGPU initialization, and shader compilation internally.
      try {
        this.engine = await webllm.CreateMLCEngine(QWEN3_MODEL_ID, {
          appConfig,
          logLevel: 'INFO',
          initProgressCallback: (progress) => {
            console.log(`📦 [Qwen3.5] ${progress.text}`);

            const percentage = progress.progress
              ? 10 + progress.progress * 80
              : 10;

            this.reportProgress({
              loaded: Math.round(percentage),
              total: 100,
              percentage,
              stage: 'downloading',
              message: progress.text || 'Loading model...',
            });
          },
        });
      } finally {
        window.fetch = originalFetch;
      }

      // Step 5: Model is ready
      const totalTime = ((performance.now() - overallStart) / 1000).toFixed(1);
      console.log(`✅ Qwen3.5-4B fully loaded and ready in ${totalTime}s`);
      this.isModelReady = true;
      this.reportProgress({
        loaded: 100, total: 100, percentage: 100,
        stage: 'ready',
        message: 'Qwen3.5-4B ready!',
      });

    } catch (error) {
      console.error('❌ Failed to initialize Qwen3.5:', error);
      this.reportProgress({
        loaded: 0, total: 100, percentage: 0,
        stage: 'checking',
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
      this.initPromise = null;
      throw error;
    }
  }

  // ========================================================================
  // Text Generation
  // ========================================================================

  /**
   * Core generation method using WebLLM's OpenAI-compatible API.
   * Replaces the old worker-based Transformers.js generation.
   */
  private async generateText(
    systemPrompt: string,
    userPrompt: string,
    enableThinking: boolean,
    options: { maxTokens?: number } = {}
  ): Promise<string> {
    if (!this.isModelReady || !this.engine) {
      throw new Error('Qwen3.5 model not initialized. Call initialize() first.');
    }

    const { maxTokens = 8192 } = options;

    // Build the messages array — WebLLM handles chat template formatting
    const messages: webllm.ChatCompletionMessageParam[] = [];

    if (systemPrompt && systemPrompt.trim().length > 0) {
      messages.push({ role: 'system', content: systemPrompt });
    }

    messages.push({ role: 'user', content: userPrompt });

    try {
      let fullOutput = '';
      let answerOutput = '';
      let thinkingOutput = '';
      let insideThinking = false;
      let numTokens = 0;
      const startTime = performance.now();

      // Use streaming for real-time output and thinking state tracking
      const stream = await this.engine.chat.completions.create({
        stream: true,
        messages,
        max_tokens: maxTokens,
        temperature: enableThinking ? 0.6 : 0.7,
        top_p: 0.95,
        presence_penalty: 1.5,            // Qwen3.5 model-card recommendation to break loops
        frequency_penalty: 0.0,
        // WebLLM supports enable_thinking for Qwen3 models
        extra_body: {
          enable_thinking: enableThinking, // Hard-disable when false (Task 3/4/5/6 all pass false)
        },
      });

      for await (const chunk of stream) {
        const delta = chunk.choices?.[0]?.delta?.content ?? '';
        if (!delta) continue;

        fullOutput += delta;
        numTokens++;

        // Track thinking vs answering state
        if (delta.includes('<think>')) {
          insideThinking = true;
        }
        if (delta.includes('</think>')) {
          insideThinking = false;
          continue; // Don't add the </think> tag to answer
        }

        if (insideThinking) {
          thinkingOutput += delta;
        } else {
          // Filter out <think> tag from answer output
          const cleanDelta = delta.replace(/<think>/g, '').replace(/<\/think>/g, '');
          answerOutput += cleanDelta;
        }

        // Repetition detection (same logic as SmolLM3Manager)
        const recentWindow = fullOutput.slice(-500);
        if (recentWindow.length >= 500) {
          const lines = recentWindow.split('\n').filter(l => l.trim().length > 0);
          if (lines.length >= 10) {
            const lastLine = lines[lines.length - 1].trim();
            const repeatedCount = lines.filter(l => l.trim() === lastLine).length;
            if (repeatedCount >= 8) {
              console.warn(`⚠️ Repetition loop detected. Interrupting.`);
              await this.engine.interruptGenerate();
              break;
            }
          }
        }
      }

      const tps = numTokens > 0
        ? (numTokens / (performance.now() - startTime)) * 1000
        : 0;
      console.log(`✅ Qwen3.5 generation complete: ${numTokens} tokens, ${tps.toFixed(1)} tok/s`);

      if (enableThinking) {
        console.log(`📊 Thinking: ${thinkingOutput.trim().length} chars, Answer: ${answerOutput.trim().length} chars`);
        if (answerOutput.trim().length === 0) {
          console.warn('⚠️ Model never exited thinking mode — using full output');
          return fullOutput;
        }
        return answerOutput;
      }

      return fullOutput;

    } catch (error) {
      console.error('❌ Qwen3.5 generation error:', error);
      throw error;
    }
  }

  // ========================================================================
  // Task Execution Methods (same interface as SmolLM3Manager)
  // ========================================================================

  public async executeTask3(systemPrompt: string, userPrompt: string): Promise<string> {
    console.log('🚀 [Qwen3.5] Executing Task #3: Query Expansion');
    console.log(`📝 System prompt: ${systemPrompt.length} chars, User prompt: ${userPrompt.length} chars`);
    console.log(`📥 [Task 3] SYSTEM PROMPT:\n${systemPrompt}`);
    console.log(`📥 [Task 3] USER PROMPT:\n${userPrompt}`);

    const result = await this.generateText(systemPrompt, userPrompt, false, {
      maxTokens: 4096,
    });

    console.log(`✅ [Qwen3.5] Task #3 complete: ${result.length} chars`);
    console.log(`📤 [Task 3] FULL OUTPUT:\n${result}`);

    if (!result.includes('###EXPANDED_QUERIES###')) {
      console.warn('⚠️ [Task 3] Output missing ###EXPANDED_QUERIES### delimiter');
    }

    return result;
  }

  /**
   * Combined Task 4' (Extract + Format) for Qwen3.5-4B desktop.
   * Returns the fully-wrapped <personal_context>…</personal_context> block plus the
   * trailing query restatement line. The caller assigns this directly to
   * task5_result_text in the SSE payload — no Task 5 LLM pass is performed.
   *
   * If the model output fails to contain a <personal_context> block, this method
   * automatically falls back to the legacy two-pass flow so the pipeline degrades
   * gracefully instead of failing the user-visible turn.
   */
  public async executeTask4(systemPrompt: string, userPrompt: string): Promise<string> {
    console.log('🚀 [Qwen3.5] Executing Task #4 (Combined Extract+Format)');
    console.log(`📝 System prompt: ${systemPrompt.length} chars, User prompt: ${userPrompt.length} chars`);
    console.log(`📥 [Task 4] SYSTEM PROMPT:\n${systemPrompt}`);
    console.log(`📥 [Task 4] USER PROMPT:\n${userPrompt}`);

    // ── PRE-FLIGHT TOKEN BUDGET CHECK (unchanged from prior Task 4) ──
    const MAX_INPUT_CHARS = 8092 * 3.5;   // ~28,322 chars within the 16384-token window
    const maxUserChars    = MAX_INPUT_CHARS - systemPrompt.length;
    if (userPrompt.length > maxUserChars) {
      console.warn('⚠️ [Task 4] User prompt exceeds budget. Truncating to fit context window.');
      let truncated = userPrompt.substring(0, Math.round(maxUserChars));
      const lastClosingTag = truncated.lastIndexOf('</source_document>');
      if (lastClosingTag > truncated.length * 0.5) {
        truncated = truncated.substring(0, lastClosingTag + '</source_document>'.length);
      }
      // Re-append the trigger section if it was cut off
      if (!truncated.includes('Output:')) {
        const outputIdx = userPrompt.lastIndexOf('Output:');
        if (outputIdx !== -1) {
          const sectionStart = userPrompt.lastIndexOf('\n\n', outputIdx);
          truncated += '\n\n' + userPrompt.substring(sectionStart !== -1 ? sectionStart : outputIdx);
        }
      }
      userPrompt = truncated;
      console.log(`📝 [Task 4] Post-truncation user prompt: ${userPrompt.length} chars`);
      console.log(`📥 [Task 4] USER PROMPT (TRUNCATED):\n${userPrompt}`);
    }

    // ── SINGLE COMBINED PASS ──
    const fullResponse = await this.generateText(systemPrompt, userPrompt, false, {
      maxTokens: 4096,    // Down from 8192; combined output empirically ~250-500 tokens
    });

    console.log('✅ [Qwen3.5] Task #4 (Combined) generation complete');
    console.log(`📤 [Task 4] FULL OUTPUT:\n${fullResponse.trim()}`);

    const cleaned = fullResponse.trim();

    // ── OUTPUT VALIDATION ──
    // The combined prompt is required to emit a <personal_context> block. If it
    // doesn't, the upstream backend's task5_result_text consumer would silently
    // ship malformed context to the external LLM. Detect and fall back instead.
    const hasOpenTag  = /<personal_context\b[^>]*>/i.test(cleaned);
    const hasCloseTag = /<\/personal_context>/i.test(cleaned);

    if (hasOpenTag && hasCloseTag) {
      console.log(`✅ [Qwen3.5] Task #4 (Combined) OK — ${cleaned.length} chars`);
      return cleaned;
    }

    console.warn(
      `⚠️ [Qwen3.5] Combined Task #4 output missing <personal_context> tags ` +
      `(open=${hasOpenTag}, close=${hasCloseTag}). Falling back to legacy two-pass.`
    );
    return ''; // Sentinel: caller (ChatbotTool) will detect empty string and run legacy path.
  }

  /**
   * Legacy two-pass Task 4 (extract bullets only). Retained for fallback.
   * Used when (a) the combined Task 4' output lacks <personal_context> tags, or
   * (b) the backend feature flag combined_task_4_5 is false.
   */
  public async executeTask4Legacy(systemPrompt: string, userPrompt: string): Promise<string> {
    console.log('🚀 [Qwen3.5] Executing legacy Task #4 (extract-only fallback)');
    console.log(`📝 System prompt: ${systemPrompt.length} chars, User prompt: ${userPrompt.length} chars`);
    console.log(`📥 [Task 4 Legacy] SYSTEM PROMPT:\n${systemPrompt}`);
    console.log(`📥 [Task 4 Legacy] USER PROMPT:\n${userPrompt}`);

    const fullResponse = await this.generateText(systemPrompt, userPrompt, false, {
      maxTokens: 4096,
    });

    const cleaned = fullResponse.split('\n## Conclusion')[0].trim();

    console.log('✅ [Qwen3.5] Task #4 (Legacy) generation complete');
    console.log(`📤 [Task 4 Legacy] FULL OUTPUT:\n${cleaned}`);

    return cleaned;
  }

  public async executeTask5(systemPrompt: string, userPrompt: string): Promise<string> {
    console.log('🚀 [Qwen3.5] Executing Task #5: Enhanced Query Generation');
    console.log(`📝 System prompt: ${systemPrompt.length} chars, User prompt: ${userPrompt.length} chars`);
    console.log(`📥 [Task 5] SYSTEM PROMPT:\n${systemPrompt}`);
    console.log(`📥 [Task 5] USER PROMPT:\n${userPrompt}`);

    const fullResponse = await this.generateText(systemPrompt, userPrompt, false, {
      maxTokens: 8192,
    });

    console.log('✅ [Qwen3.5] Task #5 generation complete');
    console.log(`📤 [Task 5] FULL OUTPUT:\n${fullResponse.trim()}`);
    
    return fullResponse.trim();
  }

  /**
   * Execute Task #6: Response Classification (Follow-up detection)
   */
  public async executeTask6(systemPrompt: string, userPrompt: string): Promise<string> {
    console.log('🚀 [Qwen3.5 Desktop] Executing Task #6: Structured Follow-Up Triage');
    console.log(`📝 System prompt: ${systemPrompt.length} chars, User prompt: ${userPrompt.length} chars`);

    // JSON output needs more than 10 tokens. Keep this still small for speed.
    const result = await this.generateText(systemPrompt, userPrompt, false, {
      maxTokens: 192,
    });

    const cleaned = result.trim();
    console.log(`✅ [Qwen3.5 Desktop] Task #6 raw result: "${cleaned}"`);
    return cleaned;
  }

  public async interruptGeneration(): Promise<void> {
    if (this.engine) {
      console.log('🛑 Interrupting Qwen3.5 generation...');
      await this.engine.interruptGenerate();
    }
  }
  
  // ========================================================================
  // Progress Reporting
  // ========================================================================

  public onProgress(callback: (progress: ModelLoadProgress) => void): () => void {
    this.progressCallbacks.add(callback);
    return () => {
      this.progressCallbacks.delete(callback);
    };
  }

  private reportProgress(progress: ModelLoadProgress): void {
    this.progressCallbacks.forEach((callback) => {
      try {
        callback(progress);
      } catch (error) {
        console.error('Error in progress callback:', error);
      }
    });
  }

  // ========================================================================
  // Status & Lifecycle
  // ========================================================================

  public isReady(): boolean {
    return this.isModelReady;
  }

  /**
   * Ping check — WebLLM engine is always responsive if loaded,
   * so this always returns true when the model is ready.
   */
  public pingWorker(timeoutMs: number = 2000): Promise<boolean> {
    return Promise.resolve(true);
  }

  public getSelectedModel(): { name: string; size: number; quantization: string } | null {
    if (!this.isModelReady) return null;
    return {
      name: 'Qwen3.5-4B-Q4F16-WebLLM',
      size: 2390 * 1024 * 1024,
      quantization: 'Q4F16',
    };
  }

  /**
   * Compatibility shim — WebLLM doesn't need a separate getProcessor() call.
   * Returns the manager itself to satisfy the App.tsx preload pattern.
   */
  public async getProcessor(): Promise<Qwen3Manager> {
    await this.initialize();
    return this;
  }

  public disposeModel(): void {
    if (this.engine) {
      this.engine.unload();
    }
  }

  public reset(): void {
    if (this.engine) {
      this.engine.unload();
      this.engine = null;
    }
    this.isModelReady = false;
    this.initPromise = null;
    console.log('🔄 Qwen3.5Manager reset');
  }

  public dispose(): void {
    this.reset();
  }
}

export default Qwen3Manager;